import { menuKindOf, normalizeTerm, type MenuKind } from '@/lib/menu-items';

export type PromoSectionKey = 'dobradas' | 'bebidas' | 'petiscos';

export interface PromoSectionDef {
  key: PromoSectionKey;
  /** Título impresso no texto copiado. */
  label: string;
  /** Categoria do cardápio que alimenta o autocomplete da seção. */
  kind: MenuKind;
  /** Prefixo aplicado a cada item na hora de formatar (ex: "2X "). */
  prefix: string;
}

export const PROMO_SECTIONS: PromoSectionDef[] = [
  { key: 'dobradas', label: 'DOBRADAS', kind: 'drink', prefix: '2X ' },
  { key: 'bebidas', label: 'BEBIDAS', kind: 'drink', prefix: '' },
  { key: 'petiscos', label: 'PETISCOS + BURGERS', kind: 'food', prefix: '' },
];

/**
 * Card de divulgação (Photoshop) — a caixa de parágrafo mede 886x833 px e o
 * texto entra em fonte monoespaçada. Como toda linha sai com exatamente o mesmo
 * número de caracteres, o pontilhado e os `R$` alinham sozinhos: basta colar,
 * sem depender da justificação do Photoshop.
 *
 * Largura do caractere medida no card de referência (corpo ~29 px numa mono de
 * avanço 0,6 = ~17,6 px por caractere).
 */
export const PROMO_CARD_BOX_W_PX = 886;
export const PROMO_CARD_BOX_H_PX = 833;
const PROMO_CARD_CHAR_W_PX = 17.6;
/**
 * Caracteres por linha que preenchem a largura da caixa de parágrafo. É a
 * largura usada em todo lugar (texto salvo, preview do modal e cópia), pra que
 * selecionar o texto na mão dê exatamente o mesmo resultado do botão Copiar.
 */
export const PROMO_CARD_LINE_CHARS = Math.floor(PROMO_CARD_BOX_W_PX / PROMO_CARD_CHAR_W_PX);

export interface PromoLine {
  /** Chave estável de render (não é persistida). */
  id: string;
  name: string;
  /** Preço como o usuário digitou ("18", "17,50") — vazio = linha incompleta. */
  price: string;
}

export type PromoSections = Record<PromoSectionKey, PromoLine[]>;

let uid = 0;
export function newPromoLine(name = '', price = ''): PromoLine {
  uid += 1;
  return { id: `promo-${uid}`, name, price };
}

export function emptyPromoSections(): PromoSections {
  return {
    dobradas: [newPromoLine()],
    bebidas: [newPromoLine()],
    petiscos: [newPromoLine()],
  };
}

/** Mantém só dígitos e uma vírgula decimal (máx. 2 casas). */
export function sanitizePriceInput(raw: string): string {
  const cleaned = raw.replace(/\./g, ',').replace(/[^\d,]/g, '');
  const [int = '', ...rest] = cleaned.split(',');
  if (rest.length === 0) return int;
  return `${int},${rest.join('').slice(0, 2)}`;
}

function priceToNumber(raw: string): number | null {
  const value = Number.parseFloat(sanitizePriceInput(raw).replace(',', '.'));
  return Number.isFinite(value) ? value : null;
}

function priceToBR(price: number): string {
  if (Number.isInteger(price)) return `R$ ${price}`;
  return `R$ ${price.toFixed(2).replace('.', ',')}`;
}

interface ResolvedLine {
  display: string;
  price: string;
}

/** Conta caracteres visíveis — acento colado de fora pode vir decomposto (NFD). */
function charLen(text: string): number {
  return [...text].length;
}

function resolveSection(def: PromoSectionDef, lines: PromoLine[]): ResolvedLine[] {
  return lines
    .map((l) => {
      const name = l.name.normalize('NFC').trim();
      const price = priceToNumber(l.price);
      if (!name || price == null) return null;
      return { display: `${def.prefix}${name}`, price: priceToBR(price) };
    })
    .filter((x): x is ResolvedLine => x !== null)
    .sort((a, b) => a.display.localeCompare(b.display, 'pt-BR', { sensitivity: 'base' }));
}

/**
 * Monta o texto final: título por categoria, itens em ordem alfabética e
 * pontilhado justificado por uma largura única (todas as seções alinhadas).
 * Seções sem item completo são omitidas.
 *
 * `lineChars` é a largura alvo da linha em caracteres — o pontilhado cresce até
 * lá. Se algum item for maior que isso, a largura sobe para caber (nunca corta).
 */
export function formatPromoSections(
  sections: PromoSections,
  lineChars = PROMO_CARD_LINE_CHARS,
): string {
  const blocks = PROMO_SECTIONS.map((def) => ({
    def,
    lines: resolveSection(def, sections[def.key]),
  })).filter((b) => b.lines.length > 0);

  if (blocks.length === 0) return '';

  const minDots = 3;
  const lineLen = Math.max(
    ...blocks.flatMap((b) =>
      b.lines.map((l) => charLen(l.display) + charLen(l.price) + minDots + 2),
    ),
    lineChars,
  );

  return blocks
    .map((b) => {
      const rows = b.lines.map((l) => {
        const dots = Math.max(minDots, lineLen - charLen(l.display) - charLen(l.price) - 2);
        return `${l.display} ${'.'.repeat(dots)} ${l.price}`;
      });
      return [b.def.label, ...rows].join('\n');
    })
    .join('\n\n');
}

/**
 * Renormaliza o texto salvo em `event.promos` na largura do card — eventos
 * antigos foram gravados numa largura menor e continuariam desalinhados.
 */
export function formatPromoTextForCard(raw: string | null | undefined): string {
  if (!raw?.trim()) return '';
  return formatPromoSections(parsePromoText(raw));
}

const PRICE_LINE = /^(.+?)[\s.…]*(?:R\$)\s*(\d+(?:[.,]\d{1,2})?)\s*$/i;
const LOOSE_LINE = /^(.+?)[\s.…]+(?:R?\$)?\s*(\d+(?:[.,]\d{1,2})?)\s*$/i;

function matchLine(line: string): { name: string; price: string } | null {
  const m = PRICE_LINE.exec(line) ?? LOOSE_LINE.exec(line);
  if (!m) return null;
  const name = (m[1] ?? '').replace(/[\s.…]+$/, '').trim();
  const price = (m[2] ?? '').replace('.', ',');
  if (!name || !price) return null;
  return { name, price };
}

/** Tira os marcadores de dobrada do nome: "2X Mojito" / "Mojito Dobrado" → "Mojito". */
export function stripDobradaMarkers(name: string): { name: string; dobrada: boolean } {
  let out = name.trim();
  let dobrada = false;
  const prefix = /^2\s*x\s+/i;
  const suffix = /\s+dobrad[oa]s?$/i;
  if (prefix.test(out)) {
    out = out.replace(prefix, '').trim();
    dobrada = true;
  }
  if (suffix.test(out)) {
    out = out.replace(suffix, '').trim();
    dobrada = true;
  }
  return { name: out, dobrada };
}

/**
 * Descobre em que categoria um item escrito à mão entra e devolve o nome limpo.
 * "CAIPIRINHA LIMÃO DOBRADA" → dobradas · "ONION RINGS" → petiscos · resto → bebidas.
 */
export function classifyPromoName(raw: string): { key: PromoSectionKey; name: string } {
  const { name, dobrada } = stripDobradaMarkers(raw);
  if (dobrada) return { key: 'dobradas', name };
  return { key: menuKindOf(name) === 'food' ? 'petiscos' : 'bebidas', name };
}

/**
 * Lê texto de promoções. Com títulos de categoria, respeita os títulos; sem
 * títulos (lista corrida colada), classifica cada linha pelo cardápio.
 */
export function parsePromoText(raw: string | null | undefined): PromoSections {
  const sections: PromoSections = { dobradas: [], bebidas: [], petiscos: [] };
  let current: PromoSectionKey | null = null;

  for (const rawLine of (raw ?? '').split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;

    const header = PROMO_SECTIONS.find(
      (def) => def.label.toUpperCase() === line.toUpperCase().replace(/\s+/g, ' '),
    );
    if (header) {
      current = header.key;
      continue;
    }

    const parsed = matchLine(line);
    if (!parsed) continue;

    if (current) {
      const name =
        current === 'dobradas' ? stripDobradaMarkers(parsed.name).name : parsed.name;
      sections[current].push(newPromoLine(name, parsed.price));
    } else {
      const { key, name } = classifyPromoName(parsed.name);
      sections[key].push(newPromoLine(name, parsed.price));
    }
  }

  for (const def of PROMO_SECTIONS) {
    if (sections[def.key].length === 0) sections[def.key].push(newPromoLine());
  }
  return sections;
}

/**
 * Distribui uma lista colada nas categorias, mantendo o que já estava lá.
 * Item repetido (mesmo nome na mesma categoria) tem só o preço atualizado.
 */
export function mergePromoText(
  base: PromoSections,
  text: string,
): { sections: PromoSections; adicionados: number } {
  const novas = parsePromoText(text);
  const sections: PromoSections = { dobradas: [], bebidas: [], petiscos: [] };
  let adicionados = 0;

  for (const def of PROMO_SECTIONS) {
    // Descarta as linhas em branco da base — elas voltam no fim se a seção ficar vazia.
    const atuais = base[def.key].filter((l) => l.name.trim() || l.price.trim());
    const porNome = new Map(atuais.map((l) => [normalizeTerm(l.name), l]));

    for (const nova of novas[def.key]) {
      if (!nova.name.trim()) continue;
      const chave = normalizeTerm(nova.name);
      const existente = porNome.get(chave);
      if (existente) {
        existente.price = nova.price;
        continue;
      }
      atuais.push(nova);
      porNome.set(chave, nova);
      adicionados += 1;
    }

    sections[def.key] = atuais.length > 0 ? atuais : [newPromoLine()];
  }

  return { sections, adicionados };
}

export function hasPromoContent(sections: PromoSections): boolean {
  return formatPromoSections(sections).length > 0;
}
